import { useEffect } from 'react';
import './LegalPages.css';

const TermsAndConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <title>Terms and Conditions - Pidesk</title>
      <meta name="description" content="Pidesk's terms and conditions for digital website services" />

      <div className="legal-page">
        <div className="container">
          <div className="legal-content">
            <h1>Terms and Conditions</h1>
            <p className="last-updated">Last updated: {new Date().toLocaleDateString()}</p>
            
            <section>
              <h2>Acceptance of Terms</h2>
              <p>
                By accessing and using Pidesk's digital website services ("Services"), you agree to be bound by these 
                Terms and Conditions ("Terms"). If you disagree with any part of these terms, you may not access or use our services.
              </p>
            </section>

            <section>
              <h2>Services Description</h2>
              <p>
                Pidesk provides digital website development and related services to organizations and individuals, including:
              </p>
              <ul>
                <li>Custom website design and development</li>
                <li>Website hosting and maintenance</li>
                <li>Digital product sales and licensing</li>
                <li>Technical support and consultation</li>
                <li>Subscription-based services</li>
              </ul>
            </section>

            <section>
              <h2>User Accounts</h2>
              <h3>Registration</h3>
              <ul>
                <li>You must provide accurate, complete, and current information during registration</li>
                <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                <li>You must be at least 18 years old or have parental consent to use our services</li>
                <li>You agree to accept responsibility for all activities under your account</li>
              </ul>

              <h3>Account Termination</h3>
              <ul>
                <li>You may terminate your account at any time by contacting our support team</li>
                <li>We reserve the right to suspend or terminate accounts for violations of these terms</li>
                <li>Termination does not relieve you of obligation to pay for services already rendered</li>
              </ul>
            </section>

            <section>
              <h2>Payment Terms</h2>
              <h3>Pricing and Payments</h3>
              <ul>
                <li>All prices are listed in USD and are subject to change without notice</li>
                <li>Payment must be made in full before service commencement unless otherwise agreed</li>
                <li>We accept payments through authorized third-party payment processors</li>
                <li>You are responsible for all taxes and fees associated with your purchases</li>
              </ul>

              <h3>Subscription Services</h3>
              <ul>
                <li>Subscriptions are billed in advance on a recurring basis</li>
                <li>You may cancel subscriptions at any time through your account settings</li>
                <li>Cancellations take effect at the end of the current billing period</li>
                <li>No refunds are provided for partial subscription periods</li>
              </ul>

              <h3>Late Payments</h3>
              <ul>
                <li>Late payments may incur interest charges of 1.5% per month</li>
                <li>Services may be suspended for accounts with overdue payments</li>
                <li>You remain responsible for collection costs and legal fees</li>
              </ul>
            </section>

            <section>
              <h2>Intellectual Property</h2>
              <h3>Our Rights</h3>
              <ul>
                <li>All content, designs, and code provided by Pidesk remain our intellectual property until full payment is received</li>
                <li>We retain the right to use completed projects in our portfolio and marketing materials</li>
                <li>Our proprietary software, tools, and methodologies remain our exclusive property</li>
              </ul>

              <h3>Your Rights</h3>
              <ul>
                <li>Upon full payment, you receive a non-exclusive license to use the delivered website</li>
                <li>You own the content you provide and upload to your website</li>
                <li>You may not resell or redistribute our proprietary code or designs</li>
                <li>Third-party licenses and components remain subject to their original terms</li>
              </ul>
            </section>

            <section>
              <h2>User Responsibilities</h2>
              <p>As a user of our services, you agree to:</p>
              <ul>
                <li>Provide accurate and complete information for your website</li>
                <li>Ensure all content complies with applicable laws and regulations</li>
                <li>Not use our services for illegal or harmful activities</li>
                <li>Not attempt to compromise our systems or other users' data</li>
                <li>Not engage in spam, phishing, or malicious activities</li>
                <li>Respect intellectual property rights of others</li>
                <li>Maintain security of your account and login credentials</li>
              </ul>
            </section>

            <section>
              <h2>Service Availability and Support</h2>
              <ul>
                <li>We strive to maintain 99.9% uptime for our hosting services</li>
                <li>Scheduled maintenance may cause temporary service interruptions</li>
                <li>Support is provided via email, phone, and our ticket system</li>
                <li>Response times vary based on service level and subscription tier</li>
                <li>We are not liable for losses due to service interruptions</li>
              </ul>
            </section>

            <section>
              <h2>Confidentiality</h2>
              <p>
                Both parties agree to maintain confidentiality of proprietary information shared during the course of business. 
                This includes but is not limited to business strategies, customer data, technical specifications, and financial information.
              </p>
            </section>

            <section>
              <h2>Limitation of Liability</h2>
              <ul>
                <li>Our total liability shall not exceed the amount paid for services in the preceding 12 months</li>
                <li>We are not liable for indirect, incidental, or consequential damages</li>
                <li>We are not responsible for third-party service interruptions or failures</li>
                <li>Our services are provided "as is" without warranties of any kind</li>
              </ul>
            </section>

            <section>
              <h2>Indemnification</h2>
              <p>
                You agree to indemnify and hold Pidesk harmless from any claims, damages, or expenses arising from:
              </p>
              <ul>
                <li>Your use of our services</li>
                <li>Content you provide or publish</li>
                <li>Violation of these terms or applicable laws</li>
                <li>Infringement of third-party rights</li>
              </ul>
            </section>

            <section>
              <h2>Termination</h2>
              <ul>
                <li>Either party may terminate these terms with 30 days written notice</li>
                <li>We may terminate immediately for material breaches</li>
                <li>Termination does not affect payment obligations for services already rendered</li>
                <li>Upon termination, you must cease using our services and return any proprietary materials</li>
              </ul>
            </section>

            <section>
              <h2>Dispute Resolution</h2>
              <ul>
                <li>Any disputes shall be governed by the laws of [Your State/Country]</li>
                <li>Parties agree to attempt good faith negotiation before legal action</li>
                <li>If unresolved, disputes will be resolved through binding arbitration</li>
                <li>Each party bears their own legal costs unless otherwise awarded</li>
              </ul>
            </section>

            <section>
              <h2>Force Majeure</h2>
              <p>
                Neither party shall be liable for failures or delays due to circumstances beyond reasonable control, 
                including natural disasters, war, terrorism, or government actions.
              </p>
            </section>

            <section>
              <h2>General Provisions</h2>
              <ul>
                <li>These terms constitute the entire agreement between parties</li>
                <li>Waiver of any term does not constitute waiver of any other term</li>
                <li>Invalid provisions shall be replaced with enforceable alternatives</li>
                <li>You may not transfer your rights or obligations without our consent</li>
                <li>We may update these terms at any time with reasonable notice</li>
              </ul>
            </section>

            <section>
              <h2>Contact Information</h2>
              <p>
                For questions about these Terms and Conditions, please contact us:
              </p>
              <ul>
                <li>Email: legal@pidesk.com</li>
                <li>Phone: +1 (555) 123-4567</li>
                <li>Address: 123 Business Ave, Suite 100, City, State 12345</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsAndConditions;
